import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { ChevronDown, Copy, History, Loader2, Plus, Trash2, X } from "lucide-react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import ColorPickerWithPalette from "../../../../components/common/ColorPickerWithPalette";
import { getSchedulingToolShiftColor } from "../../../../utils";
import { SchedulingToolUtils, type SchedulingConfig } from "../../../../utils/schedulingToolConfig";
import type { AddScheduleRequest, ScheduleV3Schedule } from "../../../../types";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../redux/store";
import { useNavigate } from "react-router";
import { setTopNavNameTitle } from "../../../../redux/features/mobileLayout/slice";
import { releaseOpenShift } from "../../../../redux/features/schedule/ScheduleApi";
import { PENDING } from "../../../../redux/constants";
import { setSelectedScheduleForCannotWork } from "../../../../redux/features/schedule/ScheduleSlice";
import { ScheduleHistoryPanel } from "../AuditLog";

export interface AddShiftModalProps {
    open: boolean;
    selectedDateForShift: Date;
    schedulingConfig: Required<SchedulingConfig>;

    isEditMode: boolean;
    isReadOnly: boolean;
    hasScheduleToolPrivilege: boolean;
    editingSchedule: ScheduleV3Schedule | null;

    formData: AddScheduleRequest & Record<string, any>;
    attendanceFormData: Record<string, any>;
    shifts: any[] | null | undefined;

    onlyNotes: boolean;
    freeFlowStartDateTime: string;
    freeFlowEndDateTime: string;
    freeFlowShiftName: string;
    selectedEmployeeOptions: Array<{ value: string; label: string }>;

    showOptionalFields: boolean;
    isLoadingAttendanceTags: boolean;
    sitesV2: any[] | null | undefined;
    departments: any[] | null | undefined;
    jobCodes: any[] | null | undefined;
    attendanceTags: any[] | null | undefined;

    isSubmitting: boolean;
    isDeleting: boolean;

    onCopy: () => void;
    onHeaderClose: () => void;
    onCancel: () => void;

    handleShiftChange: (value: number) => void;
    handleFreeFlowStartChange: (value: string) => void;
    handleFreeFlowEndChange: (value: string) => void;
    setFreeFlowShiftName: (value: string) => void;

    setSelectedEmployeeOptions: (value: Array<{ value: string; label: string }>) => void;
    loadEmployeeOptionsForSchedule: (inputValue: string, departmentCode?: string) => Promise<any[]>;
    handleInputChange: (field: string, value: any) => void;
    setShowOptionalFields: (value: boolean) => void;
    handleAttendanceFieldChange: (fieldCode: string, value: any) => void;
    handleSubmitSchedule: () => void;
    handleDeleteSchedule: () => void;
}

const AddShiftModal: React.FC<AddShiftModalProps> = ({
    open,
    selectedDateForShift,
    schedulingConfig,
    isEditMode,
    isReadOnly,
    hasScheduleToolPrivilege,
    editingSchedule,
    formData,
    attendanceFormData,
    shifts,
    onlyNotes,
    freeFlowStartDateTime,
    freeFlowEndDateTime,
    freeFlowShiftName,
    selectedEmployeeOptions,
    showOptionalFields,
    isLoadingAttendanceTags,
    sitesV2,
    departments,
    jobCodes,
    attendanceTags,
    isSubmitting,
    isDeleting,
    onCopy,
    onHeaderClose,
    onCancel,
    handleShiftChange,
    handleFreeFlowStartChange,
    handleFreeFlowEndChange,
    setFreeFlowShiftName,
    setSelectedEmployeeOptions,
    loadEmployeeOptionsForSchedule,
    handleInputChange,
    setShowOptionalFields,
    handleAttendanceFieldChange,
    handleSubmitSchedule,
    handleDeleteSchedule,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.user.user);
    const allowCustomShift = !schedulingConfig.RESTRICT_TO_PREDEFINED_SHIFTS;
    const { scheduleDetail, isScheduleDetailLoading, requestState } = useSelector(
        (state: RootState) => state.schedule
    );
    const [showReleaseModal, setShowReleaseModal] = useState(false);
    const [showActionsMenu, setShowActionsMenu] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const actionsMenuRef = useRef<HTMLDivElement>(null);

    const isOwnSchedule =
        isEditMode &&
        !!editingSchedule &&
        String(user?.os_emp_account_id ?? "") === String(editingSchedule.employee?.id ?? "");

    const publishInfo = isEditMode && editingSchedule ? SchedulingToolUtils.getPublishStatusInfo(editingSchedule) : null;
    const canChangeEmployee = isEditMode && !isReadOnly && publishInfo?.isUnpublished === true;
    const employeeSearchDepartmentCode = isEditMode ? "" : formData.department_code;
    const employeePlaceholder =
        isEditMode && selectedEmployeeOptions.length === 0
            ? editingSchedule?.employee?.name || "Search employees (min 3 characters)..."
            : "Search employees (min 3 characters)...";

    const canSwap =
        !!scheduleDetail && (scheduleDetail as any).action_button?.can_swap === true;
    const canRelease =
        !!scheduleDetail && (scheduleDetail as any).action_button?.can_release === true;
    const isReleasing = requestState === PENDING;
    const hasShiftActions = canSwap || canRelease;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
                setShowActionsMenu(false);
            }
        };

        if (showActionsMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showActionsMenu]);

    const handleSwapClicked = () => {
        if (!scheduleDetail) return;
        setShowActionsMenu(false);
        dispatch(setTopNavNameTitle("Trade Shift"));
        dispatch(setSelectedScheduleForCannotWork(scheduleDetail as any));
        navigate("/m-swap-trade");
    };

    const handleConfirmRelease = async () => {
        if (!scheduleDetail) return;
        const scheduleId = (scheduleDetail as any).schedules_id;
        if (!scheduleId) return;
        try {
            await dispatch(
                releaseOpenShift({ schedule_id: String(scheduleId) })
            ).unwrap();
            setShowReleaseModal(false);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to release shift from Scheduling Tool:", error);
        }
    };

    const handleCancelRelease = () => {
        setShowReleaseModal(false);
    };

    const handleReleaseClicked = () => {
        setShowActionsMenu(false);
        setShowReleaseModal(true);
    };

    // Close history whenever the modal closes or switches to another schedule.
    useEffect(() => {
        setShowHistory(false);
    }, [open, editingSchedule?.os_emp_schedules_id]);

    const historyScheduleId = editingSchedule?.os_emp_schedules_id;
    const canViewHistory = isEditMode && hasScheduleToolPrivilege && historyScheduleId != null;

    if (!open) return null;

    return (
        <>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1500]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-semibold text-gray-800">
                        {isEditMode ? (isReadOnly ? "View Schedule" : "Edit Schedule") : "Add Shift"} for{" "}
                        {format(selectedDateForShift, "MMMM d, yyyy")}
                        {isEditMode && editingSchedule && (
                            <div className="text-sm text-gray-500 font-normal mt-1">
                                {editingSchedule.employee?.name} - {editingSchedule.shift?.shift_name}
                                {isReadOnly && (
                                    <span className="ml-2 text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">
                                        {!hasScheduleToolPrivilege
                                            ? "Read-only (View Only)"
                                            : "Read-only (Past Date)"}
                                    </span>
                                )}
                            </div>
                        )}
                    </h2>
                    <div className="flex items-center gap-2">
                        {canViewHistory && (
                            <button
                                type="button"
                                onClick={() => setShowHistory(true)}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                title="View change history for this schedule"
                            >
                                <History size={16} />
                                History
                            </button>
                        )}
                        {/* Copy Button - Only show in edit mode and not read-only */}
                        {isEditMode && !isReadOnly && editingSchedule && (
                            <button
                                onClick={onCopy}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                                title="Copy this schedule to paste elsewhere"
                            >
                                <Copy size={16} />
                                Copy
                            </button>
                        )}
                        <button
                            onClick={onHeaderClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="p-6">
                    {/* Shift Details Section */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-700">Shift Details:</h3>
                        </div>

                        {/* Shift Dropdown */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Shift{" "}
                                {!onlyNotes && (
                                    <span className="text-gray-500 text-xs ml-1">
                                        {allowCustomShift ? "(Optional - use free flow if not selected)" : "*"}
                                    </span>
                                )}
                            </label>
                            <select
                                value={formData.os_schedules_site_shifts_id || ""}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    handleShiftChange(value ? parseInt(value) : 0);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                disabled={onlyNotes || isReadOnly}
                                required={!onlyNotes && !allowCustomShift}
                            >
                                <option value="">[--Select Shift--]</option>
                                {shifts && shifts.length > 0 ? (
                                    shifts.map((shift) => (
                                        <option
                                            key={shift.os_schedules_site_shifts_id}
                                            value={shift.os_schedules_site_shifts_id}
                                        >
                                            {shift.os_shift_name} ({shift.os_shift_start_time} -{" "}
                                            {shift.os_shift_end_time})
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>Loading shifts...</option>
                                )}
                            </select>
                            {formData.os_schedules_site_shifts_id && (
                                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="text-sm text-blue-800">
                                        <div className="font-medium mb-1">Selected Shift Details:</div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>
                                                <span className="font-semibold">Start:</span>{" "}
                                                {formData.os_emp_start_datetime}
                                            </div>
                                            <div>
                                                <span className="font-semibold">End:</span>{" "}
                                                {formData.os_emp_end_datetime}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Date inputs when shift is selected - Disabled (date/time determined by shift template) */}
                        {!isEditMode &&
                            formData.os_schedules_site_shifts_id &&
                            selectedDateForShift && (
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Start Date Time{" "}
                                            <span className="text-gray-500 text-xs">(disabled)</span>
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={freeFlowStartDateTime}
                                            disabled
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            End Date Time{" "}
                                            <span className="text-gray-500 text-xs">(disabled)</span>
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={freeFlowEndDateTime}
                                            disabled
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                                        />
                                    </div>
                                </div>
                            )}

                        {/* Free Flow Date/Time Inputs - Show when no shift selected */}
                        {!formData.os_schedules_site_shifts_id && (allowCustomShift || onlyNotes) && (
                            <>
                                {/* Shift Name field for free flow */}
                                {!onlyNotes && allowCustomShift && (
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Shift Name
                                        </label>
                                        <input
                                            type="text"
                                            value={freeFlowShiftName}
                                            onChange={(e) => setFreeFlowShiftName(e.target.value)}
                                            placeholder="Enter shift name (optional)"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {onlyNotes ? "Date Time" : "Start Date Time"}{" "}
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={freeFlowStartDateTime}
                                            onChange={(e) => handleFreeFlowStartChange(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        />
                                    </div>
                                    {!onlyNotes && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                End Date Time <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="datetime-local"
                                                value={freeFlowEndDateTime}
                                                onChange={(e) => handleFreeFlowEndChange(e.target.value)}
                                                min={freeFlowStartDateTime || undefined}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Employee Selection Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">
                            {isEditMode ? "Employee" : "Employees"}{" "}
                            <span className="text-red-500">*</span>
                        </h3>

                        <AsyncSelect
                            cacheOptions={employeeSearchDepartmentCode || "all"}
                            defaultOptions={
                                selectedEmployeeOptions.length > 0 ? selectedEmployeeOptions : undefined
                            }
                            isMulti={!isEditMode}
                            isDisabled={isEditMode && !canChangeEmployee}
                            value={isEditMode ? selectedEmployeeOptions[0] || null : selectedEmployeeOptions}
                            onChange={(selected: any) => {
                                if (isEditMode && !canChangeEmployee) return;
                                const selectedArray = Array.isArray(selected)
                                    ? selected
                                    : selected
                                      ? [selected]
                                      : [];
                                setSelectedEmployeeOptions(selectedArray);
                                const selectedIds = selectedArray
                                    .map((item: any) => Number(item.value))
                                    .filter((id) => !Number.isNaN(id));
                                handleInputChange("os_emp_account_id", selectedIds);
                            }}
                            loadOptions={(inputValue) =>
                                loadEmployeeOptionsForSchedule(inputValue, employeeSearchDepartmentCode)
                            }
                            placeholder={employeePlaceholder}
                            noOptionsMessage={({ inputValue }) =>
                                inputValue.length < 3
                                    ? "Type at least 3 characters to search"
                                    : "No employees found"
                            }
                            className="text-sm"
                            classNamePrefix="react-select"
                            isClearable={!isEditMode}
                            menuPortalTarget={document.body}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 1600 }),
                            }}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {isEditMode
                                ? canChangeEmployee
                                    ? "Employee can be changed for unpublished schedules"
                                    : "Employee cannot be changed for published schedules"
                                : "Select one or more employees to assign this schedule to"}
                        </p>
                    </div>

                    {/* Assignment Details Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">
                            Assignment Details:
                        </h3>

                        {!isEditMode && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Occurrence Type
                                    </label>
                                    <select
                                        value={formData.occurence_type || "DAY"}
                                        onChange={(e) =>
                                            handleInputChange("occurence_type", e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        disabled={onlyNotes}
                                    >
                                        <option value="DAY">Days</option>
                                        <option value="WEEK">Weeks</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Number of{" "}
                                        {formData.occurence_type === "WEEK" ? "weeks" : "days"}
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.add_days || ""}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === "" || value === "-") {
                                                handleInputChange("add_days", null as any);
                                                return;
                                            }

                                            const numValue = parseInt(value, 10);
                                            if (!isNaN(numValue) && numValue > 0) {
                                                handleInputChange("add_days", numValue);
                                            }
                                        }}
                                        onBlur={() => {
                                            const currentValue = formData.add_days;
                                            if (
                                                !currentValue ||
                                                currentValue < 1 ||
                                                isNaN(currentValue as any)
                                            ) {
                                                handleInputChange("add_days", 1);
                                            }
                                        }}
                                        min="1"
                                        placeholder="1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        disabled={onlyNotes || isReadOnly}
                                    />
                                </div>
                            </div>
                        )}

                        <button
                            onClick={() => setShowOptionalFields(!showOptionalFields)}
                            className="mt-4 text-blue-500 hover:text-blue-600 text-sm font-medium flex items-center gap-1"
                        >
                            <Plus size={16} />
                            Optional fields
                        </button>
                    </div>

                    {/* Optional Fields Section */}
                    {showOptionalFields && (
                        <div className="mb-6 border-t border-gray-200 pt-6">
                            <h3 className="text-lg font-semibold text-gray-700 mb-4">
                                Optional fields
                            </h3>

                            {/* Site Dropdown - Only show if sites exist */}
                            {sitesV2 && sitesV2.length > 0 && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Site
                                    </label>
                                    <Select
                                        value={
                                            formData.us_user_site_profile_id
                                                ? sitesV2?.find(
                                                      (s) =>
                                                          s.us_user_site_profile_id ===
                                                          formData.us_user_site_profile_id
                                                  )
                                                    ? {
                                                          value: formData.us_user_site_profile_id,
                                                          label:
                                                              sitesV2.find(
                                                                  (s) =>
                                                                      s.us_user_site_profile_id ===
                                                                      formData.us_user_site_profile_id
                                                              )?.user_site_name || "",
                                                      }
                                                    : null
                                                : null
                                        }
                                        onChange={(selected: any) => {
                                            handleInputChange(
                                                "us_user_site_profile_id",
                                                selected ? parseInt(selected.value) : undefined
                                            );
                                        }}
                                        options={
                                            sitesV2
                                                ?.filter((site) => site.us_user_site_profile_id)
                                                .map((site) => ({
                                                    value: site.us_user_site_profile_id!,
                                                    label: site.user_site_name || "",
                                                })) || []
                                        }
                                        className="text-sm"
                                        classNamePrefix="react-select"
                                        placeholder="Select Site"
                                        isClearable
                                        isSearchable
                                        isDisabled={isReadOnly}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 1500 }),
                                            menu: (base) => ({ ...base, zIndex: 1500 }),
                                        }}
                                    />
                                </div>
                            )}

                            {/* Department */}
                            {departments && departments.length > 0 && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Department
                                    </label>
                                    <Select
                                        value={
                                            formData.department_code
                                                ? departments?.find(
                                                      (d) => d.department_code === formData.department_code
                                                  )
                                                    ? {
                                                          value: formData.department_code,
                                                          label:
                                                              departments.find(
                                                                  (d) =>
                                                                      d.department_code === formData.department_code
                                                              )?.department_name || "",
                                                      }
                                                    : null
                                                : null
                                        }
                                        onChange={(selected: any) => {
                                            handleInputChange(
                                                "department_code",
                                                selected ? selected.value : undefined
                                            );
                                        }}
                                        options={
                                            departments
                                                ?.filter((dept) => dept.department_code && dept.department_name)
                                                .map((dept) => ({
                                                    value: dept.department_code!,
                                                    label: dept.department_name!,
                                                })) || []
                                        }
                                        className="text-sm"
                                        classNamePrefix="react-select"
                                        placeholder="Select Department"
                                        isClearable
                                        isSearchable
                                        isDisabled={isReadOnly}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 1500 }),
                                            menu: (base) => ({ ...base, zIndex: 1500 }),
                                        }}
                                    />
                                </div>
                            )}

                            {/* Job Code (Work Code) */}
                            {jobCodes && jobCodes.length > 0 && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Job Code
                                    </label>
                                    <Select
                                        value={
                                            formData.os_work_codes_id
                                                ? jobCodes?.find(
                                                      (j) => j.os_work_codes_id === formData.os_work_codes_id
                                                  )
                                                    ? {
                                                          value: formData.os_work_codes_id,
                                                          label: `${
                                                              jobCodes.find(
                                                                  (j) =>
                                                                      j.os_work_codes_id === formData.os_work_codes_id
                                                              )?.work_code_name
                                                          } (${
                                                              jobCodes.find(
                                                                  (j) =>
                                                                      j.os_work_codes_id === formData.os_work_codes_id
                                                              )?.work_code_number
                                                          })`,
                                                      }
                                                    : null
                                                : null
                                        }
                                        onChange={(selected: any) => {
                                            handleInputChange(
                                                "os_work_codes_id",
                                                selected ? parseInt(selected.value) : undefined
                                            );
                                        }}
                                        options={
                                            jobCodes
                                                ?.filter(
                                                    (jobCode) => jobCode.os_work_codes_id && jobCode.work_code_name
                                                )
                                                .map((jobCode) => ({
                                                    value: jobCode.os_work_codes_id!,
                                                    label: `${jobCode.work_code_name} (${jobCode.work_code_number})`,
                                                })) || []
                                        }
                                        className="text-sm"
                                        classNamePrefix="react-select"
                                        placeholder="Select Job Code"
                                        isClearable
                                        isSearchable
                                        isDisabled={isReadOnly}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 1500 }),
                                            menu: (base) => ({ ...base, zIndex: 1500 }),
                                        }}
                                    />
                                </div>
                            )}

                            {/* Shift Color */}
                            <div className="mb-4">
                                <ColorPickerWithPalette
                                    value={formData.shift_color || getSchedulingToolShiftColor()}
                                    onChange={(color) => handleInputChange("shift_color", color)}
                                    label="Shift Color (Optional)"
                                />
                            </div>

                            {/* Notes */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notes
                                </label>
                                <textarea
                                    rows={3}
                                    value={
                                        formData.logs === null ||
                                        formData.logs === "null" ||
                                        formData.logs === undefined
                                            ? ""
                                            : formData.logs || ""
                                    }
                                    onChange={(e) => handleInputChange("logs", e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Add any additional notes..."
                                />
                            </div>

                            {/* Attendance Tags Section */}
                            {showOptionalFields && (
                                <div className="mb-4 space-y-4 border-t border-gray-200 pt-4">
                                    <h4 className="text-sm font-semibold text-gray-700">
                                        Attendance Tags:
                                    </h4>
                                    {isLoadingAttendanceTags ? (
                                        <div className="flex items-center justify-center py-8">
                                            <Loader2 className="animate-spin text-blue-500" size={24} />
                                            <span className="ml-2 text-sm text-gray-600">
                                                Loading attendance tags...
                                            </span>
                                        </div>
                                    ) : attendanceTags && attendanceTags.length > 0 ? (
                                        attendanceTags.map((tag) => {
                                            const fieldType = tag.field_type?.toLowerCase();
                                            const isMultiselect = tag.multiselect === "true";

                                            const getOptions = (
                                                f: typeof tag
                                            ): { value_code: string; value_text: string }[] => {
                                                if (!f.field_json) return [];

                                                if (Array.isArray(f.field_json)) {
                                                    return f.field_json.map((item: any) => ({
                                                        value_code:
                                                            item.field_value_code ||
                                                            item.value_code ||
                                                            item.code ||
                                                            "",
                                                        value_text:
                                                            item.field_value_text ||
                                                            item.value_text ||
                                                            item.text ||
                                                            item.name ||
                                                            "",
                                                    }));
                                                }

                                                if (typeof f.field_json === "string") {
                                                    try {
                                                        const arr = JSON.parse(f.field_json);
                                                        if (Array.isArray(arr)) {
                                                            return arr.map((item: any) => ({
                                                                value_code:
                                                                    item.field_value_code ||
                                                                    item.value_code ||
                                                                    item.code ||
                                                                    "",
                                                                value_text:
                                                                    item.field_value_text ||
                                                                    item.value_text ||
                                                                    item.text ||
                                                                    item.name ||
                                                                    "",
                                                            }));
                                                        }
                                                    } catch (error) {
                                                        return [];
                                                    }
                                                }

                                                return [];
                                            };

                                            const options = getOptions(tag);
                                            const isRequired = tag.required === "true";

                                            const fieldValue = attendanceFormData[tag.field_code] || [];

                                            if (fieldType === "dropdown" && options.length > 0) {
                                                if (isMultiselect) {
                                                    const selectOptions = options.map((opt) => ({
                                                        value: opt.value_code,
                                                        label: opt.value_text,
                                                    }));

                                                    const selectedValues = Array.isArray(fieldValue)
                                                        ? fieldValue.map((v: any) => v.value_code || v)
                                                        : [];

                                                    const selectedOptions = selectOptions.filter((opt) =>
                                                        selectedValues.includes(opt.value)
                                                    );

                                                    return (
                                                        <div key={tag.field_code}>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                {tag.field_name}
                                                                {isRequired && (
                                                                    <span className="text-red-500 ml-1">*</span>
                                                                )}
                                                            </label>
                                                            <Select
                                                                isMulti
                                                                value={selectedOptions}
                                                                onChange={(selected) => {
                                                                    const selectedArray = selected as Array<{
                                                                        value: string;
                                                                        label: string;
                                                                    }> | null;
                                                                    if (selectedArray) {
                                                                        const newValues = selectedArray.map((item) => ({
                                                                            value_code: item.value,
                                                                            value_text: item.label,
                                                                        }));
                                                                        handleAttendanceFieldChange(
                                                                            tag.field_code,
                                                                            newValues
                                                                        );
                                                                    } else {
                                                                        handleAttendanceFieldChange(tag.field_code, []);
                                                                    }
                                                                }}
                                                                options={selectOptions}
                                                                placeholder={`Select an option`}
                                                                className="text-sm"
                                                                classNamePrefix="react-select"
                                                                styles={{
                                                                    control: (base) => ({
                                                                        ...base,
                                                                        minHeight: "38px",
                                                                        fontSize: "14px",
                                                                    }),
                                                                    menu: (base) => ({
                                                                        ...base,
                                                                        fontSize: "14px",
                                                                    }),
                                                                }}
                                                            />
                                                            {tag.field_description && (
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    {tag.field_description}
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                const currentValue =
                                                    Array.isArray(fieldValue) && fieldValue.length > 0
                                                        ? fieldValue[0].value_code
                                                        : "";

                                                return (
                                                    <div key={tag.field_code}>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                                            {tag.field_name}
                                                            {isRequired && (
                                                                <span className="text-red-500 ml-1">*</span>
                                                            )}
                                                        </label>
                                                        <select
                                                            value={currentValue}
                                                            onChange={(e) => {
                                                                const selectedOption = options.find(
                                                                    (opt) => opt.value_code === e.target.value
                                                                );
                                                                if (selectedOption) {
                                                                    handleAttendanceFieldChange(tag.field_code, [
                                                                        {
                                                                            value_code: selectedOption.value_code,
                                                                            value_text: selectedOption.value_text,
                                                                        },
                                                                    ]);
                                                                } else {
                                                                    handleAttendanceFieldChange(tag.field_code, []);
                                                                }
                                                            }}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        >
                                                            <option value="">
                                                                Select {tag.field_name}...
                                                            </option>
                                                            {options.map((option, index) => (
                                                                <option key={index} value={option.value_code}>
                                                                    {option.value_text}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {tag.field_description && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {tag.field_description}
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            }

                                            const currentTextValue =
                                                Array.isArray(fieldValue) && fieldValue.length > 0
                                                    ? fieldValue[0].value_text
                                                    : "";

                                            return (
                                                <div key={tag.field_code}>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {tag.field_name}
                                                        {isRequired && (
                                                            <span className="text-red-500 ml-1">*</span>
                                                        )}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={currentTextValue}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            handleAttendanceFieldChange(
                                                                tag.field_code,
                                                                value
                                                                    ? [
                                                                          {
                                                                              value_code: tag.field_code,
                                                                              value_text: value,
                                                                          },
                                                                      ]
                                                                    : []
                                                            );
                                                        }}
                                                        placeholder={`Add details`}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                    {tag.field_description && (
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {tag.field_description}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-sm text-gray-500 py-4">
                                            No attendance tags available.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Delete button - only show in edit mode and not read-only */}
                        {isEditMode && !isReadOnly ? (
                            <>
                                <button
                                    onClick={(e) => {
                                        console.log("Delete button clicked (event)");
                                        e.preventDefault();
                                        handleDeleteSchedule();
                                    }}
                                    disabled={
                                        isSubmitting ||
                                        isDeleting ||
                                        (selectedDateForShift &&
                                            !SchedulingToolUtils.canEditDate(
                                                selectedDateForShift,
                                                schedulingConfig
                                            ))
                                    }
                                    className="h-10 px-4 bg-transparent border border-red-500 text-red-600 text-sm rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    type="button"
                                    title={
                                        selectedDateForShift &&
                                        !SchedulingToolUtils.canEditDate(selectedDateForShift, schedulingConfig)
                                            ? "Cannot delete shifts for past dates"
                                            : "Delete schedule"
                                    }
                                >
                                    {isDeleting ? (
                                        <>
                                            <Loader2 className="animate-spin" size={16} />
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 size={16} />
                                            Delete
                                        </>
                                    )}
                                </button>

                                {(isScheduleDetailLoading || hasShiftActions) && (
                                    <div className="relative" ref={actionsMenuRef}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (!isScheduleDetailLoading && hasShiftActions) {
                                                    setShowActionsMenu((current) => !current);
                                                }
                                            }}
                                            disabled={isScheduleDetailLoading || isSubmitting || isDeleting || isReleasing}
                                            className="h-10 px-4 bg-transparent border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                                        >
                                            {isScheduleDetailLoading ? (
                                                <>
                                                    <Loader2 className="animate-spin" size={16} />
                                                    Actions
                                                </>
                                            ) : (
                                                <>
                                                    Actions
                                                    <ChevronDown size={16} className={`transition-transform ${showActionsMenu ? "rotate-180" : ""}`} />
                                                </>
                                            )}
                                        </button>

                                        {showActionsMenu && !isScheduleDetailLoading && hasShiftActions && (
                                            <div className="absolute bottom-full left-0 mb-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg z-[1600] overflow-hidden">
                                                {canSwap && (
                                                    <button
                                                        type="button"
                                                        onClick={handleSwapClicked}
                                                        className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                                                    >
                                                        Trade / Swap Shift
                                                    </button>
                                                )}
                                                {canRelease && (
                                                    <button
                                                        type="button"
                                                        onClick={handleReleaseClicked}
                                                        disabled={isReleasing}
                                                        className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {isReleasing ? "Releasing…" : "Release Shift"}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div />
                        )}
                    </div>

                    {/* Right side buttons */}
                    <div className="flex gap-3 ml-auto">
                        <button
                            onClick={onCancel}
                            disabled={isSubmitting || isDeleting}
                            className="px-6 py-2 bg-transparent border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        {!isReadOnly && (
                            <button
                                onClick={handleSubmitSchedule}
                                disabled={
                                    isSubmitting ||
                                    isDeleting ||
                                    isLoadingAttendanceTags ||
                                    (isEditMode &&
                                        selectedDateForShift &&
                                        !SchedulingToolUtils.canEditDate(
                                            selectedDateForShift,
                                            schedulingConfig
                                        ))
                                }
                                className="px-6 py-2 bg-transparent border border-blue-500 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                title={
                                    isEditMode &&
                                    selectedDateForShift &&
                                    !SchedulingToolUtils.canEditDate(selectedDateForShift, schedulingConfig)
                                        ? "Cannot edit shifts for past dates"
                                        : undefined
                                }
                            >
                                {isSubmitting && <Loader2 className="animate-spin" size={16} />}
                                {isSubmitting
                                    ? isEditMode
                                        ? "Updating..."
                                        : "Assigning..."
                                    : isEditMode
                                      ? "Update Schedule"
                                      : "Assign Shift"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
        {canViewHistory && (
            <ScheduleHistoryPanel
                open={showHistory}
                onClose={() => setShowHistory(false)}
                scheduleId={historyScheduleId}
                subtitle={[editingSchedule?.employee?.name, editingSchedule?.shift?.shift_name].filter(Boolean).join(" · ")}
                zIndexClass="z-[1600]"
            />
        )}
        </>
    );
};

export default AddShiftModal;
